import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, differenceInYears } from 'date-fns';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export function isWeekend(date: Date): boolean {
    return [6, 0].includes(new Date(date).getDay());
}

export function isValidDate(dateString: string) {
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
    const timezoneRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}[+-]\d{2}:\d{2}$/; // e.g., 2023-10-05T14:48:00.000000+02:00
    const isoMillisRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
    const alternativeTimezoneRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]?\d{2}:\d{2}$/; // e.g., 2023-10-05T14:48:00.000+02:00
    if (
        !dateFormatRegex.test(dateString) &&
        !timezoneRegex.test(dateString) &&
        !isoMillisRegex.test(dateString) &&
        !alternativeTimezoneRegex.test(dateString)
    ) {
        return false;
    }
    const dateObject = new Date(dateString);
    return !isNaN(dateObject.getTime());
}

export function calculateDaysBetweenDates(startDate: Date, endDate: Date): number {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
    const daysDiff = Math.ceil(timeDiff / millisecondsPerDay);
    return daysDiff;
}

export function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export enum DateStep {
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
    YEAR = 'year',
    FULL_PERIOD = 'full_period',
    CUSTOM = 'custom'
}

export function humanReadableDateStep(step: DateStep): React.ReactNode {
    switch (step) {
        case DateStep.DAY:
            return (
                <FormattedMessage
                    id='date-step.day'
                    defaultMessage='Last 24 hours'
                />
            );
        case DateStep.WEEK:
            return (
                <FormattedMessage
                    id='date-step.week'
                    defaultMessage='Last 7 days'
                />
            );
        case DateStep.MONTH:
            return (
                <FormattedMessage
                    id='date-step.month'
                    defaultMessage='Last 30 days'
                />
            );
        case DateStep.YEAR:
            return (
                <FormattedMessage
                    id='date-step.year'
                    defaultMessage='Last 365 days'
                />
            );
        case DateStep.FULL_PERIOD:
            return (
                <FormattedMessage
                    id='date-step.full-period'
                    defaultMessage='Full period'
                />
            );
        case DateStep.CUSTOM:
            return (
                <FormattedMessage
                    id='date-step.custom'
                    defaultMessage='Custom'
                />
            );
        default:
            return (
                <FormattedMessage
                    id='date-step.unknown'
                    defaultMessage='Unknown'
                />
            );
    }
}

export function convertDateStep(step: DateStep): string | undefined {
    switch (step) {
        case DateStep.WEEK:
            return 'week';
        case DateStep.MONTH:
            return 'month';
        default:
            return undefined;
    }
}

export function convertTimestampToDayMonthYear(timestamp: string): string {
    const d = new Date(timestamp);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${day}/${month}/${year}`;
}

export function calculateTimeDifference(dateString: string): React.ReactNode {
    const now = new Date();
    const pastDate = new Date(dateString);
    const differenceInMilliseconds = now.getTime() - pastDate.getTime();

    const seconds = Math.floor(differenceInMilliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
        return (
            <FormattedMessage
                id='dates.days-ago'
                defaultMessage='{count, plural, one {# day} other {# days}} ago'
                values={{ count: days }}
            />
        );
    } else if (hours > 0) {
        return (
            <FormattedMessage
                id='dates.hours-ago'
                defaultMessage='{count, plural, one {# hour} other {# hours}} ago'
                values={{ count: hours }}
            />
        );
    } else if (minutes > 0) {
        return (
            <FormattedMessage
                id='dates.minutes-ago'
                defaultMessage='{count, plural, one {# minute} other {# minutes}} ago'
                values={{ count: minutes }}
            />
        );
    } else {
        return (
            <FormattedMessage
                id='dates.less-than-1-minute'
                defaultMessage='Less than 1 minute ago'
            />
        );
    }
}

export function calculateTimeDifferenceBetweenDates(startDate: string, endDate: string): React.ReactNode {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const totalSeconds = differenceInSeconds(end, start);
    const totalMinutes = differenceInMinutes(end, start);
    const totalHours = differenceInHours(end, start);
    const totalDays = differenceInDays(end, start);
    const years = differenceInYears(end, start);

    // Get remaining components after subtracting larger units
    const seconds = totalSeconds % 60;
    const minutes = totalMinutes % 60;
    const hours = totalHours % 24;
    const days = totalDays % 365;

    if (totalSeconds < 60) {
        return (
            <FormattedMessage
                id='dates.seconds'
                defaultMessage='{count, plural, one {# second} other {# seconds}}'
                values={{ count: seconds }}
            />
        );
    } else if (totalMinutes < 60) {
        return (
            <FormattedMessage
                id='dates.minutes'
                defaultMessage='{count, plural, one {# minute} other {# minutes}}'
                values={{ count: minutes }}
            />
        );
    } else if (totalHours < 24) {
        return (
            <FormattedMessage
                id='dates.hours'
                defaultMessage='{count, plural, one {# hour} other {# hours}}'
                values={{ count: hours }}
            />
        );
    } else if (totalDays < 365) {
        return (
            <FormattedMessage
                id='dates.days'
                defaultMessage='{count, plural, one {# day} other {# days}}'
                values={{ count: days }}
            />
        );
    } else {
        return (
            <FormattedMessage
                id='dates.years'
                defaultMessage='{count, plural, one {# year} other {# years}}'
                values={{ count: years }}
            />
        );
    }
}

export function formatTimeDifference(startTimestamp: string, endTimestamp: string): string {
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    let diffMs = end.getTime() - start.getTime();

    if (diffMs < 0) diffMs = 0;

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const pad = (n: number) => String(n).padStart(2, '0');

    if (days > 0) {
        return `${days}d ${pad(hours)}:${pad(minutes)}`;
    } else {
        return `${pad(hours)}:${pad(minutes)}`;
    }
}

export enum DateFormat {
    HH_MM_DD_MMMM = 'HH:mm • DD MMMM',
    DD_MMMM_HH_MM = 'DD MMMM • HH:mm',
    DD_MMM = 'DD MMM',
    DD_MMMM_YYYY = 'DD MMMM YYYY',
    DD_MM_YYYY = 'DD/MM/YYYY',
    DD_MM_YYYY_HH_MM = 'DD/MM/YYYY • HH:mm',
    HH_MM_SS_MS = 'HH:mm:ss.SSS'
}
export function formatDateTimeWithLocale(date: Date, locale: string = 'en-US', format: DateFormat = DateFormat.HH_MM_DD_MMMM): string {
    switch (format) {
        case DateFormat.HH_MM_DD_MMMM:
            const formatter = new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: 'long',
                hour12: false
            });

            const parts = formatter.formatToParts(date);

            const hour = parts.find((p) => p.type === 'hour')?.value;
            const minute = parts.find((p) => p.type === 'minute')?.value;
            const day = parts.find((p) => p.type === 'day')?.value;
            const month = parts.find((p) => p.type === 'month')?.value;

            return `${hour}:${minute} • ${day} ${month}`;
        case DateFormat.DD_MMMM_HH_MM: {
            const formatter = new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: 'long',
                hour12: false
            });

            const parts = formatter.formatToParts(date);

            const hour = parts.find((p) => p.type === 'hour')?.value;
            const minute = parts.find((p) => p.type === 'minute')?.value;
            const day = parts.find((p) => p.type === 'day')?.value;
            const month = parts.find((p) => p.type === 'month')?.value;

            return `${day} ${month} • ${hour}:${minute}`;
        }
        case DateFormat.DD_MMM:
            const ddmmmFormatter = new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'short'
            });

            const ddmmmParts = ddmmmFormatter.formatToParts(date);

            const ddmmmDay = ddmmmParts.find((p) => p.type === 'day')?.value;
            const ddmmmMonth = ddmmmParts.find((p) => p.type === 'month')?.value;

            return `${ddmmmDay} ${ddmmmMonth}`;
        case DateFormat.DD_MMMM_YYYY:
            const ddmmmmFormatter = new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

            const ddmmmmParts = ddmmmmFormatter.formatToParts(date);

            const ddmmmmDay = ddmmmmParts.find((p) => p.type === 'day')?.value;
            const ddmmmmMonth = ddmmmmParts.find((p) => p.type === 'month')?.value;
            const ddmmmmYear = ddmmmmParts.find((p) => p.type === 'year')?.value;

            return `${ddmmmmDay} ${ddmmmmMonth} ${ddmmmmYear}`;
        case DateFormat.DD_MM_YYYY:
            const ddmmyyyyFormatter = new Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });

            const ddmmyyyyParts = ddmmyyyyFormatter.formatToParts(date);

            const ddmmyyyyDay = ddmmyyyyParts.find((p) => p.type === 'day')?.value;
            const ddmmyyyyMonth = ddmmyyyyParts.find((p) => p.type === 'month')?.value;
            const ddmmyyyyYear = ddmmyyyyParts.find((p) => p.type === 'year')?.value;

            return `${ddmmyyyyDay}/${ddmmyyyyMonth}/${ddmmyyyyYear}`;
        case DateFormat.DD_MM_YYYY_HH_MM: {
            const dateFormatter = new Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
            const timeFormatter = new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            const dateParts = dateFormatter.formatToParts(date);
            const timeParts = timeFormatter.formatToParts(date);
            const day = dateParts.find((p) => p.type === 'day')?.value;
            const month = dateParts.find((p) => p.type === 'month')?.value;
            const year = dateParts.find((p) => p.type === 'year')?.value;
            const hour = timeParts.find((p) => p.type === 'hour')?.value;
            const minute = timeParts.find((p) => p.type === 'minute')?.value;
            return `${day}/${month}/${year} • ${hour}:${minute}`;
        }
        case DateFormat.HH_MM_SS_MS: {
            const formatter = new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
                ['fractionalSecondDigits' as any]: 3
            });

            const parts = formatter.formatToParts(date);

            const hour = parts.find((p) => p.type === 'hour')?.value;
            const minute = parts.find((p) => p.type === 'minute')?.value;
            const second = parts.find((p) => p.type === 'second')?.value;
            const msec = parts.find((p) => (p.type as string) === 'fractionalSecond')?.value;

            return `${hour}:${minute}:${second}.${msec}`;
        }
        default:
            return date.toLocaleDateString(locale);
    }
}

export function convertSecondsToDays(seconds: number): React.ReactNode {
    const days = Math.floor(seconds / (60 * 60 * 24));
    return (
        <FormattedMessage
            id='dates.days-from-seconds'
            defaultMessage='{count, plural, one {# day} other {# days}}'
            values={{ count: days }}
        />
    );
}

export function formatDays(days: number): React.ReactNode {
    return (
        <FormattedMessage
            id='dates.days-from-seconds'
            defaultMessage='{count, plural, one {# day} other {# days}}'
            values={{ count: days }}
        />
    );
}

export interface DateRangeAdjustment {
    fromDate: Date;
    toDate: Date;
}

/**
 * Adjusts date range when user selects a date that would create an invalid range.
 * - If selecting a "from" date after the "to" date, moves "to" forward by the original interval
 * - If selecting a "to" date before the "from" date, moves "from" backward by the original interval
 * - Preserves the original time interval between dates when adjusting
 */
export const adjustDateRange = (currentFrom: string | null, currentTo: string | null, newDateStr: string, dateType: 'from' | 'to'): DateRangeAdjustment => {
    // If both dates are null, just set the selected one
    if (!currentFrom && !currentTo) {
        const newDate = new Date(newDateStr);
        if (dateType === 'from') {
            return { fromDate: newDate, toDate: newDate };
        } else {
            return { fromDate: newDate, toDate: newDate };
        }
    }

    const oldFrom = new Date(currentFrom ?? newDateStr);
    const oldTo = new Date(currentTo ?? newDateStr);
    const interval = oldTo.getTime() - oldFrom.getTime();
    let newFrom: Date = oldFrom;
    let newTo: Date = oldTo;

    if (dateType === 'from') {
        newFrom = new Date(newDateStr);
        // If new from date is after current to date, adjust to date forward
        if (newFrom.getTime() > oldTo.getTime()) {
            newTo = new Date(newFrom.getTime() + Math.abs(interval));
        }
    } else {
        newTo = new Date(newDateStr);
        // If new to date is before current from date, adjust from date backward
        if (newTo.getTime() < oldFrom.getTime()) {
            newFrom = new Date(newTo.getTime() - Math.abs(interval));
        }
    }

    return { fromDate: newFrom, toDate: newTo };
};
